import { Grid, Typography } from '@mui/material';
import { WithStyles, useTheme, withStyles } from '@mui/styles';
import { CancelButtonGrey } from '@tassat/button';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { ApiConnectionFirmMapping } from '@tassat/fred-extended-definitions';
import {
    Email as EmailIcon,
    Lock as LockIcon,
    Phone as PhoneIcon,
    Source as SourceIcon
} from '@tassat/icon';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import history from 'Lib/browserHistory';
import { LOCK_ICON_COLOR } from 'Lib/constant';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';
import ActionsDropdownMenu from 'SharedComponents/actionsDropdownMenu/actionsDropdownMenu';

interface ApiConnectionFirmDetailsProps extends WithStyles<typeof muiStyles> {
    apiConnectionFirm: ApiConnectionFirmMapping;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    selectedApiContainer: {
        position: 'relative' as const,
        width: '100%',
        backgroundColor: theme?.app?.applicationColors.enumColors.white ||
            '#fff',
        padding: '2rem 3.5rem'
    },
    apiConnectionNameContainer: { marginBottom: '.5rem' },
    apiConnectionName: {
        fontSize: '1.5rem',
        color: theme?.app?.applicationColors.fontColor ||
            '#202020',
        paddingRight: '.75rem'
    },
    apiConnectionDetail: {
        lineHeight: '1.4rem',
        color: theme?.app?.applicationColors.fontColor ||
            '#202020'
    },
    lockStatusIcon: { alignSelf: 'center' as const },
    cancelButton: {
        position: 'absolute' as const,
        alignSelf: 'flex-end' as const
    }
});

export function ApiConnectionFirmDetails (props: ApiConnectionFirmDetailsProps) {
    const {
        classes,
        apiConnectionFirm
    } = props;

    const theme = useTheme<ApplicationMuiTheme>();

    const apiConnection = apiConnectionFirm.apiConnection;

    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const lockFirmApiConnection = async (apiFirmConnection: ApiConnectionFirmMapping) => {
        const { APIConnectionFirmId, IsLocked } = apiFirmConnection.apiFirm;
        const { APIConnectionName } = apiFirmConnection.apiConnection;
        const { Name } = apiFirmConnection.firm;

        try {
            const response = await Worker.getInstance()
                .updateAPIConnectionFirm({
                    APIConnectionFirmId: APIConnectionFirmId,
                    IsLocked: !IsLocked
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `${APIConnectionName} successfully ${IsLocked ? 'unlocked' : 'locked'}`,
                        successBody: `Successfully ${IsLocked ? 'unlocked' : 'locked'} use of ${APIConnectionName} for ${Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${IsLocked ? 'unlocked' : 'locked'} ${APIConnectionName}`,
                        failureBody: `Could not ${IsLocked ? 'unlocked' : 'locked'} ${APIConnectionName} for ${Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${IsLocked ? 'unlocked' : 'locked'} ${APIConnectionName}`,
                    failureBody: `Something went wrong ${IsLocked ? 'unlocking' : 'locking'} ${APIConnectionName} for ${Name}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const lockFirmApiConnectionValidation = (apiFirmConnection: ApiConnectionFirmMapping): boolean => {
        const { IsLocked } = apiFirmConnection.apiFirm;
        const { APIConnectionName } = apiFirmConnection.apiConnection;
        const { Name } = apiFirmConnection.firm;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiFirmConnection,
                actionType: IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `${IsLocked ? 'Unlock' : 'Lock'} ${Name} on ${APIConnectionName}`,
                confirmBody: `Please confirm ${IsLocked ? 'unlocking' : 'locking'} ${Name} for use on ${APIConnectionName}.${!IsLocked ? `\nWARNING: This will prevent all users of this Firm from using ${theme?.app?.clientTheme.names.applicationName || 'TassatPay'} API Services via this API Connection.` : ''} `
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return true;
    };

    const deleteFirmApiConnection = async (apiFirmConnection: ApiConnectionFirmMapping) => {
        const { APIConnectionFirmId } = apiFirmConnection.apiFirm;
        const { APIConnectionName } = apiFirmConnection.apiConnection;
        const { Name } = apiFirmConnection.firm;

        try {
            const response = await Worker.getInstance()
                .deleteAPIConnectionFirm(APIConnectionFirmId);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Deleted Firm Connection ${APIConnectionName}`,
                        successBody: `Successfully deleted use of ${APIConnectionName} by the firm ${Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Delete Firm Connection ${APIConnectionName}`,
                        failureBody: `Could not delete use of ${APIConnectionName} by the firm ${Name}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Delete Firm Connection ${APIConnectionName}`,
                    failureBody: `Something went wrong deleting use of ${APIConnectionName} by the firm ${Name}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const deleteFirmApiConnectionValidation = (apiFirmConnection: ApiConnectionFirmMapping): boolean => {
        const { APIConnectionName } = apiFirmConnection.apiConnection;
        const { Name } = apiFirmConnection.firm;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiFirmConnection,
                actionType: ActionTypes.delete,
                confirmTitle: `Delete Firm Connection ${APIConnectionName}`,
                confirmBody: `Please confirm deleting the use of ${APIConnectionName} by the firm ${Name}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return true;
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: apiConnectionFirm,
                setSelectedItem: () => apiConnectionFirm,
                preActionValidationAndConfig: {
                    [ActionTypes.lock]: lockFirmApiConnectionValidation,
                    [ActionTypes.unlock]: lockFirmApiConnectionValidation,
                    [ActionTypes.delete]: deleteFirmApiConnectionValidation
                },
                onConfirm: {
                    [ActionTypes.lock]: lockFirmApiConnection,
                    [ActionTypes.unlock]: lockFirmApiConnection,
                    [ActionTypes.delete]: deleteFirmApiConnection
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.selectedApiContainer}
                container
                direction='column'
                item
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    className={classes.cancelButton}
                    item
                >
                    <CancelButtonGrey onClick={() => history.push(`/admin/firms/${apiConnectionFirm.firm.FirmId}`)} />
                </Grid>
                <Grid
                    className={classes.apiConnectionNameContainer}
                    container
                    direction='row'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Typography
                        className={classes.apiConnectionName}
                        variant='h3'
                    >
                        <strong>{apiConnection.APIConnectionName}</strong>
                    </Typography>
                    {apiConnectionFirm.apiFirm.IsLocked &&
                        <LockIcon
                            className={classes.lockStatusIcon}
                            iconColor={LOCK_ICON_COLOR}
                        />}
                    <ActionsDropdownMenu
                        data={apiConnectionFirm}
                        locked={apiConnectionFirm.apiFirm.IsLocked}
                    />
                </Grid>
                {
                    apiConnection.SourceId &&
                    <Grid
                        container
                        item
                        spacing={1}
                    >
                        <Grid item>
                            <SourceIcon id='apiConnectionFirmDetailsSource' />
                        </Grid>
                        <Grid item>
                            <Typography variant='body1'>
                                {apiConnection.SourceId}
                            </Typography>
                        </Grid>
                    </Grid>
                }
                {
                    apiConnection.APIFirmEmail &&
                    <Grid
                        container
                        item
                        spacing={1}
                    >
                        <Grid item>
                            <EmailIcon id='apiConnectionFirmDetailsEmail' />
                        </Grid>
                        <Grid item>
                            <Typography variant='body1'>
                                {apiConnection.APIFirmEmail}
                            </Typography>
                        </Grid>
                    </Grid>
                }
                {
                    apiConnection.APIFirmPhone &&
                    <Grid
                        container
                        item
                        spacing={1}
                    >
                        <Grid item>
                            <PhoneIcon id='apiConnectionFirmDetailsPhone' />
                        </Grid>
                        <Grid item>
                            <Typography variant='body1'>
                                {apiConnection.APIFirmPhone}
                            </Typography>
                        </Grid>
                    </Grid>
                }
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}

export default withStyles(muiStyles)(ApiConnectionFirmDetails);